import { useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { NotificationBell } from '../NotificationBell';
import { PlaneTakeoff as CrewFlightsIcon, BookOpen as CrewLogIcon, ShieldAlert as CrewSmsIcon, Bell as CrewBellIcon } from 'lucide-react';
import { 
    LayoutDashboard, Plane, PlaneTakeoff, Users, Wrench, 
    Settings, LogOut, Bell, Menu, X, Moon, Sun,
    FileText, MessageSquare, AlertTriangle, BarChart3, ClipboardList, Activity, CalendarRange, UserCheck, Shield, Package, CalendarDays, Ticket, Radio, CreditCard, DollarSign, Brain, Calendar, Headphones, Lock
} from 'lucide-react';
import { Button } from '../ui/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { Avatar, AvatarFallback } from '../ui/avatar';
import { Badge } from '../ui/badge';
import { ContractModuleButton } from '../ModuleUpsell';

// Bloque C: pilotos y tripulantes de cabina solo ven su portal
const CREW_NAV = [
    { path: '/mis-vuelos', icon: CrewFlightsIcon, label: 'Mis vuelos', roles: ['pilot', 'cabin_crew'] },
    { path: '/mi-bitacora', icon: CrewLogIcon, label: 'Mi bitácora', roles: ['pilot'] },
    { path: '/sms', icon: CrewSmsIcon, label: 'Reportes SMS', roles: ['pilot', 'cabin_crew'] },
    { path: '/notifications', icon: CrewBellIcon, label: 'Notificaciones', roles: ['pilot', 'cabin_crew'] },
];

const navItems = [
    { path: '/dashboard', icon: LayoutDashboard, label: 'Dashboard', roles: ['admin', 'pilot', 'dispatcher', 'maintenance', 'ccm', 'ccod', 'crew_admin', 'counter', 'ramp', 'comercial', 'ventas', 'gerencia', 'analista', 'calidad'] },
    { path: '/gh', icon: Package, label: 'Ground Handling', roles: ['gh_supervisor', 'gh_leader'] },
    { path: '/schedule', icon: CalendarRange, label: 'Programacion', roles: ['admin', 'comercial', 'ccod', 'gerencia', 'crew_admin'] },
    { path: '/cco', icon: Activity, label: 'Centro de Control', roles: ['admin', 'dispatcher', 'ccod', 'gerencia'] },
    { path: '/flights', icon: Plane, label: 'Vuelos', roles: ['admin', 'dispatcher', 'ccod', 'pilot', 'comercial', 'counter', 'crew_admin', 'gerencia', 'analista'] },
    { path: '/fleet', icon: PlaneTakeoff, label: 'Flota', roles: ['admin', 'maintenance', 'dispatcher', 'ccod', 'gerencia', 'analista'] },
    { path: '/crew', icon: Users, label: 'Tripulaciones', module: 'crew', roles: ['admin', 'crew_admin', 'pilot', 'ccod', 'gerencia'] },
    { path: '/counter', icon: UserCheck, label: 'Mostrador', roles: ['admin', 'counter', 'ccod'] },
    { path: '/ventas', icon: Ticket, label: 'Ventas', roles: ['admin', 'comercial', 'ventas', 'counter', 'gerencia'] },
    { path: '/dispatch', icon: Shield, label: 'Despacho de Vuelo', roles: ['admin', 'dispatcher', 'ccod'] },
    { path: '/ramp', icon: Package, label: 'Rampa', roles: ['admin', 'ramp', 'ccod', 'dispatcher'] },
    { path: '/turnaround', icon: Activity, label: 'Turnaround', roles: ['admin', 'ccod', 'dispatcher', 'ramp', 'counter', 'gerencia', 'maintenance', 'crew_admin'] },
    { path: '/mensajes', icon: Radio, label: 'Mensajes Op.', roles: ['admin', 'pilot', 'dispatcher', 'maintenance', 'ccm', 'ccod', 'crew_admin', 'counter', 'ramp', 'comercial', 'ventas', 'gerencia', 'analista', 'calidad'] },
    { path: '/maintenance', icon: Wrench, label: 'Mantenimiento', module: 'maintenance', roles: ['admin', 'maintenance'] },
    { path: '/predictive-mx', icon: Calendar, label: 'Mtto. Predictivo', module: 'predictive_maintenance', roles: ['admin', 'maintenance', 'ccod', 'dispatcher', 'gerencia'] },
    { path: '/deferrals', icon: AlertTriangle, label: 'Diferidos', module: 'maintenance', roles: ['admin', 'maintenance', 'ccm', 'ccod', 'dispatcher'] },
    { path: '/documents', icon: FileText, label: 'Documentos', roles: ['admin', 'maintenance', 'crew_admin', 'gerencia'] },
    { path: '/logbook', icon: ClipboardList, label: 'Bitacora', module: 'crew', roles: ['admin', 'pilot', 'dispatcher'] },
    { path: '/crew-calendar', icon: CalendarDays, label: 'Calendario Crew', module: 'crew', roles: ['admin', 'crew_admin', 'ccod', 'dispatcher', 'gerencia'] },
    { path: '/sms', icon: AlertTriangle, label: 'SMS Seguridad', roles: ['admin', 'ccod', 'gerencia', 'analista', 'dispatcher', 'pilot', 'maintenance', 'crew_admin', 'calidad'] },
    { path: '/reports', icon: BarChart3, label: 'Reportes', roles: ['admin', 'ccod', 'gerencia', 'analista', 'maintenance', 'crew_admin'] },
    { path: '/statistics', icon: BarChart3, label: 'Estadisticas', roles: ['admin', 'dispatcher', 'ccod', 'gerencia', 'analista', 'comercial'] },
    { path: '/revenue', icon: DollarSign, label: 'Revenue & Yield', module: 'revenue', roles: ['admin', 'gerencia', 'analista', 'comercial'] },
    { path: '/ai-agents', icon: Brain, label: 'Agentes IA', module: 'ai_agents', roles: ['admin', 'ccod', 'dispatcher', 'gerencia', 'analista', 'comercial', 'crew_admin', 'pilot', 'maintenance', 'ccm'] },
    { path: '/billing', icon: CreditCard, label: 'Facturacion', roles: ['admin', 'gerencia'] },
    { path: '/support', icon: Headphones, label: 'Soporte', roles: ['admin', 'gh_supervisor'] },
    { path: '/users', icon: Users, label: 'Mi Equipo', roles: ['admin'] },
];

export const Sidebar = () => {
    const [collapsed, setCollapsed] = useState(false);
    const [mobileOpen, setMobileOpen] = useState(false);
    const { user, airline, logout, hasRole, hasModule } = useAuth();
    const { theme, toggleTheme } = useTheme();
    const location = useLocation();
    const navigate = useNavigate();

    const isCrew = ['pilot', 'cabin_crew'].includes(user?.role);
    const filteredNav = (isCrew ? CREW_NAV : navItems).filter(item => hasRole(item.roles));
    const isLocked = (item) => item.module && !hasModule(item.module);

    const getInitials = (firstName, lastName) => {
        return `${firstName?.[0] || ''}${lastName?.[0] || ''}`.toUpperCase();
    };

    const getRoleBadge = (role) => {
        const roleLabels = {
            admin: 'Administrador',
            pilot: 'Tripulación de vuelo',
            dispatcher: 'Despacho de Vuelo',
            maintenance: 'Mantenimiento',
            ccm: 'CCM', cabin_crew: 'Tripulante de cabina',
            ccod: 'CCO',
            crew_admin: 'Admin Tripulaciones',
            counter: 'Mostrador',
            ramp: 'Rampa',
            comercial: 'Comercial',
            ventas: 'Ventas',
            gerencia: 'Direccion Ejecutiva',
            analista: 'Analista',
            calidad: 'Control de Calidad',
            gh_supervisor: 'Supervisor Ground Handling',
            gh_leader: 'Lider de Equipo GH',
        };
        return roleLabels[role] || role;
    };

    const SidebarContent = () => (
        <div className="flex flex-col h-full">
            {/* Header */}
            <div className="p-4 border-b border-border/50">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                        <Plane className="w-6 h-6 text-primary" />
                    </div>
                    {!collapsed && (
                        <div className="animate-fade-in flex-1">
                            <h1 className="text-lg font-bold tracking-tight uppercase font-['Barlow_Condensed']">
                                AeroOps
                            </h1>
                            <p className="text-xs text-muted-foreground truncate max-w-[140px]">
                                {airline?.name || 'Control de Operaciones'}
                            </p>
                        </div>
                    )}
                    {!collapsed && <NotificationBell />}
                </div>
            </div>

            {/* Navigation */}
            <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
                {filteredNav.map((item) => {
                    const Icon = item.icon;
                    const isActive = location.pathname === item.path;
                    
                    const locked = isLocked(item);
                    return (
                        <div key={item.path} className="group relative flex items-center">
                            <NavLink
                                to={item.path}
                                data-testid={`nav-${item.path.replace('/', '')}`}
                                className={`sidebar-item relative flex-1 min-w-0 ${isActive ? 'active' : ''} ${locked ? 'opacity-60' : ''}`}
                                onClick={() => setMobileOpen(false)}
                                title={locked ? `${item.label}: módulo no contratado` : item.label}
                            >
                                {isActive && <div className="nav-active-indicator" />}
                                <Icon className="w-5 h-5 flex-shrink-0" />
                                {!collapsed && (
                                    <span className="animate-fade-in text-sm font-medium truncate">
                                        {item.label}
                                    </span>
                                )}
                                {locked && <Lock className={`w-3.5 h-3.5 text-amber-500 flex-shrink-0 ${collapsed ? 'absolute top-1 right-1' : 'ml-auto'}`} data-testid={`nav-lock-${item.path.replace('/', '')}`} />}
                            </NavLink>
                            {locked && !collapsed && user?.role === 'admin' && (
                                <div className="absolute right-1 top-1/2 -translate-y-1/2 opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto focus-within:opacity-100 focus-within:pointer-events-auto transition-opacity">
                                    <ContractModuleButton moduleKey={item.module} size="sm" className="h-6 px-2 text-[10px] uppercase shadow" />
                                </div>
                            )}
                        </div>
                    );
                })}
            </nav>

            {/* Footer */}
            <div className="p-3 border-t border-border/50 space-y-2">
                {/* Theme Toggle */}
                <button
                    onClick={toggleTheme}
                    data-testid="theme-toggle"
                    className={`sidebar-item w-full justify-${collapsed ? 'center' : 'start'}`}
                >
                    {theme === 'dark' ? (
                        <Sun className="w-5 h-5" />
                    ) : (
                        <Moon className="w-5 h-5" />
                    )}
                    {!collapsed && <span className="text-sm">Cambiar tema</span>}
                </button>

                {/* User Menu */}
                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <button 
                            data-testid="user-menu-trigger"
                            className={`sidebar-item w-full justify-${collapsed ? 'center' : 'start'}`}
                        >
                            <Avatar className="w-8 h-8">
                                <AvatarFallback className="bg-primary/10 text-primary text-xs">
                                    {getInitials(user?.first_name, user?.last_name)}
                                </AvatarFallback>
                            </Avatar>
                            {!collapsed && (
                                <div className="text-left animate-fade-in">
                                    <p className="text-sm font-medium truncate max-w-[120px]">
                                        {user?.first_name} {user?.last_name}
                                    </p>
                                    <p className="text-xs text-muted-foreground">
                                        {getRoleBadge(user?.role)}
                                    </p>
                                </div>
                            )}
                        </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-56">
                        <DropdownMenuLabel>Mi Cuenta</DropdownMenuLabel>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={() => navigate('/settings')} data-testid="menu-settings">
                            <Settings className="w-4 h-4 mr-2" />
                            Configuración
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => navigate('/notifications')} data-testid="menu-notifications">
                            <Bell className="w-4 h-4 mr-2" />
                            Notificaciones
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem 
                            onClick={logout}
                            data-testid="logout-btn"
                            className="text-destructive focus:text-destructive"
                        >
                            <LogOut className="w-4 h-4 mr-2" />
                            Cerrar Sesión
                        </DropdownMenuItem>
                    </DropdownMenuContent>
                </DropdownMenu>

                {/* Collapse Toggle (Desktop) */}
                <button
                    onClick={() => setCollapsed(!collapsed)}
                    className="sidebar-item w-full justify-center hidden md:flex"
                    data-testid="sidebar-collapse"
                >
                    <Menu className="w-5 h-5" />
                </button>
            </div>
        </div>
    );

    return (
        <>
            {/* Mobile Menu Button */}
            <button
                onClick={() => setMobileOpen(true)}
                className="fixed top-4 left-4 z-50 p-2 rounded-lg bg-card border border-border md:hidden"
                data-testid="mobile-menu-btn"
            >
                <Menu className="w-6 h-6" />
            </button>

            {/* Mobile Overlay */}
            {mobileOpen && (
                <div
                    className="fixed inset-0 bg-background/80 backdrop-blur-sm z-40 md:hidden"
                    onClick={() => setMobileOpen(false)}
                />
            )}

            {/* Mobile Sidebar */}
            <aside
                className={`fixed inset-y-0 left-0 z-50 w-64 bg-card border-r border-border transform transition-transform duration-300 md:hidden ${
                    mobileOpen ? 'translate-x-0' : '-translate-x-full'
                }`}
            >
                <button
                    onClick={() => setMobileOpen(false)}
                    className="absolute top-4 right-4 p-1"
                >
                    <X className="w-5 h-5" />
                </button>
                <SidebarContent />
            </aside>

            {/* Desktop Sidebar */}
            <aside
                className={`hidden md:flex flex-col bg-card border-r border-border transition-all duration-300 ${
                    collapsed ? 'w-[72px]' : 'w-[260px]'
                }`}
                data-testid="sidebar"
            >
                <SidebarContent />
            </aside>
        </>
    );
};
